import { useEffect, useState } from "react";
import { api, errMsg, exportFile } from "../lib/api";
import { fmtMoney } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { StatCard } from "../components/StatCard";
import { DataTable } from "../components/DataTable";
import { inputCls, selectCls, btnSecondary } from "../components/Modal";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, Wallet, PiggyBank, FileSpreadsheet, FileDown, Truck, BadgePercent, HandCoins, Clock } from "lucide-react";

const COLORS = ["#2563EB", "#D97706", "#16A34A", "#7C3AED", "#DC2626", "#0891B2", "#DB2777", "#65A30D"];

export default function Reports() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [group, setGroup] = useState("day");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [stock, setStock] = useState(null);
  const [overview, setOverview] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [asyncReport, setAsyncReport] = useState("sales");
  const [asyncFormat, setAsyncFormat] = useState("xlsx");

  const loadJobs = () => api.get("/exports").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { loadJobs(); }, []);
  useEffect(() => {
    if (!jobs.some((j) => j.status === "pending" || j.status === "processing")) return;
    const t = setInterval(loadJobs, 4000);
    return () => clearInterval(t);
  }, [jobs]);

  const launchAsync = async () => {
    try {
      await api.post("/exports", { report: asyncReport, format: asyncFormat, date_from: from, date_to: to });
      toast.success("Export lancé en arrière-plan — vous serez notifié quand il sera prêt");
      loadJobs();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = () => {
    api.get("/reports/sales", { params: { date_from: from, date_to: to, group } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/reports/stock").then((r) => setStock(r.data)).catch(() => {});
    api.get("/reports/overview", { params: { date_from: from, date_to: to } }).then((r) => setOverview(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [group]);

  const totals = data ? data.rows.reduce((a, r) => ({ total: a.total + r.total, count: a.count + r.count, profit: a.profit + (r.profit || 0) }), { total: 0, count: 0, profit: 0 }) : null;

  const sellerCols = [
    { key: "seller", label: "Vendeur", render: (r) => <span className="font-semibold">{r.seller}</span> },
    { key: "count", label: "Ventes", className: "text-right" },
    { key: "total", label: "CA", className: "text-right", render: (r) => <span className="font-money font-bold">{fmtMoney(r.total, cur)}</span> },
  ];

  const periodCols = [
    { key: "period", label: "Période", render: (r) => <span className="font-money font-bold">{r.period}</span> },
    { key: "count", label: "Ventes", className: "text-right" },
    { key: "total", label: "CA", className: "text-right", render: (r) => <span className="font-money font-bold">{fmtMoney(r.total, cur)}</span> },
    { key: "paid", label: "Encaissé", className: "text-right", render: (r) => <span className="font-money text-emerald-600">{fmtMoney(r.paid, cur)}</span> },
    ...(hasPerm("reports.financial") ? [{ key: "profit", label: "Marge", className: "text-right", render: (r) => <span className="font-money text-amber-600 font-bold">{fmtMoney(r.profit || 0, cur)}</span> }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Rapports</h1>
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Du</div>
            <input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Au</div>
            <input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Grouper par</div>
            <select data-testid="report-group" value={group} onChange={(e) => setGroup(e.target.value)} className={selectCls}>
              <option value="day">Jour</option>
              <option value="month">Mois</option>
              <option value="year">Année</option>
            </select>
          </div>
          <button data-testid="report-apply" onClick={load} className={btnSecondary}>Appliquer</button>
        </div>
      </div>

      {hasPerm("reports.export") && (
        <div className="flex gap-2 flex-wrap" data-testid="report-exports">
          <button data-testid="export-sales-xlsx" onClick={() => exportFile(`/reports/export?report=sales&format=xlsx&date_from=${from}&date_to=${to}`)} className={btnSecondary}><FileSpreadsheet className="h-4 w-4" /> Ventes Excel</button>
          <button data-testid="export-sales-csv" onClick={() => exportFile(`/reports/export?report=sales&format=csv&date_from=${from}&date_to=${to}`)} className={btnSecondary}><FileDown className="h-4 w-4" /> Ventes CSV</button>
          <button data-testid="export-sales-pdf" onClick={() => exportFile(`/reports/export?report=sales&format=pdf&date_from=${from}&date_to=${to}`)} className={btnSecondary}><FileDown className="h-4 w-4" /> Ventes PDF</button>
          <button data-testid="export-stock-xlsx" onClick={() => exportFile("/reports/export?report=stock&format=xlsx")} className={btnSecondary}><FileSpreadsheet className="h-4 w-4" /> Stock Excel</button>
          <button data-testid="export-expenses-csv" onClick={() => exportFile("/reports/export?report=expenses&format=csv")} className={btnSecondary}><FileDown className="h-4 w-4" /> Dépenses CSV</button>
          <button data-testid="export-movements-csv" onClick={() => exportFile("/reports/export?report=movements&format=csv")} className={btnSecondary}><FileDown className="h-4 w-4" /> Mouvements CSV</button>
        </div>
      )}

      {hasPerm("reports.export") && (
        <div className="bg-card border rounded-xl p-4 space-y-3" data-testid="async-exports">
          <h2 className="font-heading font-bold">Exports lourds en arrière-plan</h2>
          <p className="text-xs text-muted-foreground">Le fichier est généré en tâche de fond et stocké de façon persistante. Une notification vous prévient quand il est prêt.</p>
          <div className="flex gap-2 flex-wrap items-center">
            <select data-testid="async-report" value={asyncReport} onChange={(e) => setAsyncReport(e.target.value)} className={selectCls}>
              <option value="sales">Ventes</option>
              <option value="stock">Stock</option>
              <option value="expenses">Dépenses</option>
              <option value="movements">Mouvements</option>
            </select>
            <select data-testid="async-format" value={asyncFormat} onChange={(e) => setAsyncFormat(e.target.value)} className={selectCls}>
              <option value="xlsx">Excel</option>
              <option value="csv">CSV</option>
              <option value="pdf">PDF</option>
            </select>
            <button data-testid="async-launch" onClick={launchAsync} className={btnSecondary}><Clock className="h-4 w-4" /> Lancer en tâche de fond</button>
          </div>
          {jobs.length > 0 && (
            <div className="divide-y border rounded-lg" data-testid="async-jobs">
              {jobs.map((j) => (
                <div key={j.id} className="flex items-center gap-3 px-3 py-2 text-sm flex-wrap" data-testid={`async-job-${j.id}`}>
                  <span className="font-semibold">{j.report_label} — {j.format.toUpperCase()}</span>
                  <span className="text-xs text-muted-foreground">{new Date(j.created_at).toLocaleString("fr-FR")} · {j.created_by_name}</span>
                  <span className="flex-1" />
                  {j.status === "done" ? (
                    <button data-testid={`async-dl-${j.id}`} onClick={() => exportFile(`/exports/${j.id}/download`)} className="text-xs font-bold text-primary hover:underline">Télécharger</button>
                  ) : j.status === "failed" ? (
                    <span data-testid={`async-failed-${j.id}`} className="text-xs font-bold text-rose-600" title={j.error}>Échec</span>
                  ) : (
                    <span data-testid={`async-running-${j.id}`} className="text-xs font-bold text-amber-600 animate-pulse">En cours...</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="report-overview">
          <StatCard testid="rep-ov-sales" label="Ventes" value={fmtMoney(overview.sales_total, cur)} icon={TrendingUp} tone="blue" sub={`${overview.sales_count} opération(s)`} />
          <StatCard testid="rep-ov-purchases" label="Achats" value={fmtMoney(overview.purchases_total, cur)} icon={Truck} tone="slate" />
          <StatCard testid="rep-ov-expenses" label="Dépenses" value={fmtMoney(overview.expenses_total, cur)} icon={Wallet} tone="rose" />
          {overview.margin !== undefined && <StatCard testid="rep-ov-margin" label="Marge brute" value={fmtMoney(overview.margin, cur)} icon={PiggyBank} tone="amber" sub={overview.net_estimate !== undefined ? `Net estimé: ${fmtMoney(overview.net_estimate, cur)}` : undefined} />}
          <StatCard testid="rep-ov-credits" label="Créances clients" value={fmtMoney(overview.credits_total, cur)} icon={BadgePercent} tone="amber" />
          <StatCard testid="rep-ov-debts" label="Dettes fournisseurs" value={fmtMoney(overview.debts_total, cur)} icon={HandCoins} tone="rose" />
        </div>
      )}

      {data && totals && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard testid="rep-ca" label="Chiffre d'affaires" value={fmtMoney(totals.total, cur)} icon={TrendingUp} tone="blue" />
            <StatCard testid="rep-count" label="Nombre de ventes" value={totals.count} icon={Wallet} tone="slate" />
            {hasPerm("reports.financial") && <StatCard testid="rep-profit" label="Marge brute" value={fmtMoney(totals.profit, cur)} icon={PiggyBank} tone="amber" />}
            {stock?.total_value != null && <StatCard testid="rep-stock-value" label="Valorisation stock" value={fmtMoney(stock.total_value, cur)} icon={PiggyBank} tone="emerald" />}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-card border rounded-xl p-4">
              <h2 className="font-heading font-bold mb-3">CA par période</h2>
              <div className="h-64" data-testid="report-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.rows}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                    <Tooltip formatter={(v) => fmtMoney(v, cur)} />
                    <Bar dataKey="total" fill="#2563EB" radius={[6, 6, 0, 0]} name="CA" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-card border rounded-xl p-4">
              <h2 className="font-heading font-bold mb-3">CA par catégorie</h2>
              <div className="h-64" data-testid="report-pie">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.by_category} dataKey="total" nameKey="category" innerRadius={50} outerRadius={85} label={({ category }) => category}>
                      {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtMoney(v, cur)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <DataTable testid="report-periods" columns={periodCols} rows={[...data.rows].reverse()} searchKeys={["period"]} pageSize={15} />

          <h2 className="font-heading font-bold text-lg">Performance par vendeur</h2>
          <DataTable testid="report-sellers" columns={sellerCols} rows={data.by_seller} searchKeys={["seller"]} empty="Aucune vente" />
        </>
      )}
    </div>
  );
}
